import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import useCountUp from "@/hooks/useCountUp";
import {
  Megaphone, Send, CheckCheck, Eye, Plus, Loader2, Upload, ArrowRight, ArrowLeft,
  Users, Clock, Image as ImageIcon, Search, CheckSquare, Square, Tag as TagIcon,
  Info, RotateCw, Download, Sheet, FileSpreadsheet, FileText,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
const labelCls = "text-xs tracking-[0.2em] uppercase font-bold text-slate-500";
const primaryBtn = "bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60";
const secondaryBtn = "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2";

const sectionVariants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((h) => h.trim());
  const rows = lines.slice(1).map((l) => {
    const cols = l.split(",").map((c) => c.trim().replace(/["']/g, ""));
    const obj = {}; headers.forEach((h, i) => (obj[h] = cols[i] || "")); return obj;
  });
  return { headers, rows };
}

// Same person written differently (+91 98765 43210 / 919876543210 / 09876543210) shares
// the last 10 digits — that's the duplicate key. Fewer than 10 digits can't be a real number.
const mobileDigits = (v) => String(v ?? "").replace(/\D/g, "");
const mobileKey = (v) => mobileDigits(v).slice(-10);

// White-background campaign summary drawn on a canvas and downloaded as PNG — sized
// 1080×1350 (portrait) so it reads well when forwarded on WhatsApp.
function downloadCampaignPng(c, branchName, times) {
  const W = 1080, H = 1350, P = 70;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const x = cv.getContext("2d");
  const font = (size, weight = 400) => `${weight} ${size}px "Segoe UI", Roboto, Arial, sans-serif`;
  const box = (bx, by, bw, bh, r, fill) => {
    x.beginPath();
    x.moveTo(bx + r, by); x.arcTo(bx + bw, by, bx + bw, by + bh, r); x.arcTo(bx + bw, by + bh, bx, by + bh, r);
    x.arcTo(bx, by + bh, bx, by, r); x.arcTo(bx, by, bx + bw, by, r); x.closePath();
    x.fillStyle = fill; x.fill();
  };
  const fit = (text, maxW) => {
    let t = String(text ?? "");
    while (t.length > 1 && x.measureText(t).width > maxW) t = t.slice(0, -2) + "…";
    return t;
  };

  x.fillStyle = "#ffffff"; x.fillRect(0, 0, W, H);

  // Header band
  x.fillStyle = "#ea580c"; x.fillRect(0, 0, W, 190);
  x.fillStyle = "#ffffff";
  x.font = font(30, 700); x.fillText("ADIYASH GYM" + (branchName ? `  ·  ${branchName}` : ""), P, 80);
  x.font = font(56, 800); x.fillText("Campaign Report", P, 150);

  // Campaign name + template
  x.fillStyle = "#0f172a"; x.font = font(48, 800);
  x.fillText(fit(c.name, W - 2 * P), P, 280);
  x.fillStyle = "#64748b"; x.font = font(30);
  x.fillText(fit(`Template: ${c.template_name}  ·  Status: ${c.status}`, W - 2 * P), P, 330);

  // Time boxes (2 × 2)
  const tget = (k) => (times.find(([label]) => label === k) || [])[1] || "—";
  const tItems = [["Created", tget("Created")], ["Started", tget("Started")],
                  ["Finished", tget("Finished")], ["Took", tget("Took") !== "—" ? tget("Took") : tget("Running for")]];
  const bw = (W - 2 * P - 24) / 2, bh = 110;
  tItems.forEach(([k, v], i) => {
    const bx = P + (i % 2) * (bw + 24), by = 380 + Math.floor(i / 2) * (bh + 20);
    box(bx, by, bw, bh, 18, "#f1f5f9");
    x.fillStyle = "#64748b"; x.font = font(24, 700); x.fillText(k.toUpperCase(), bx + 28, by + 42);
    x.fillStyle = "#0f172a"; x.font = font(36, 700); x.fillText(fit(v, bw - 56), bx + 28, by + 88);
  });

  // Count rows with bars (bar = share of total)
  const total = c.total_count || 0;
  const rows = [
    ["Total", total, "#0f172a"],
    ["Sent", c.sent_count, "#2563eb"],
    ["Delivered", c.delivered_count, "#16a34a"],
    ["Read", c.read_count, "#0ea5e9"],
    ["Failed", c.failed_count, "#dc2626"],
  ];
  let y = 690;
  rows.forEach(([k, n, color]) => {
    const share = total ? n / total : 0;
    x.fillStyle = "#334155"; x.font = font(32, 700); x.fillText(k, P, y);
    x.fillStyle = color; x.font = font(40, 800);
    const label = k === "Total" ? `${n}` : `${n}  (${Math.round(share * 100)}%)`;
    x.textAlign = "right"; x.fillText(label, W - P, y); x.textAlign = "left";
    box(P, y + 18, W - 2 * P, 18, 9, "#e2e8f0");
    if (share > 0) box(P, y + 18, Math.max(18, (W - 2 * P) * share), 18, 9, color);
    y += 100;
  });

  // Rates
  const rate = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
  x.fillStyle = "#0f172a"; x.font = font(30, 700);
  x.fillText(`Delivery rate ${rate(c.delivered_count, c.sent_count)}   ·   Read rate ${rate(c.read_count, c.delivered_count)}`, P, y + 20);

  // Estimated spend (delivered x rate + GST; an estimate, not Meta billing)
  const ec = c.est_cost;
  if (ec) {
    x.fillStyle = "#0f172a"; x.font = font(30, 700);
    x.fillText(`Estimated spend ₹${ec.total.toFixed(2)}   (${ec.delivered} delivered × ₹${ec.rate} + 18% GST)`, P, y + 70);
  }

  // Footer
  x.fillStyle = "#94a3b8"; x.font = font(24);
  x.fillText(`Generated ${new Date().toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}  ·  app.adiyashgym.in`, P, H - 50);

  cv.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${c.name || "campaign"} report.png`; a.click();
    URL.revokeObjectURL(url);
  }, "image/png");
}

function StatCard({ icon: Icon, label, value, testid }) {
  const display = useCountUp(value);
  return (
    <motion.div
      variants={sectionVariants}
      data-testid={testid}
      className="bg-[#141414] border border-white/10 hover:border-white/20 rounded-lg shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30"
    >
      <div className="flex items-center justify-between">
        <p className={labelCls}>{label}</p>
        <div className="h-8 w-8 rounded-md bg-orange-500/10 flex items-center justify-center"><Icon className="h-4 w-4 text-orange-600" /></div>
      </div>
      <p className="font-heading text-2xl font-black text-white mt-2">{display}</p>
    </motion.div>
  );
}

export default function Broadcasting() {
  const { activeBranchId, activeBranch } = useBranch();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({ total_campaigns: 0, total_sent: 0, delivered: 0, read: 0 });

  const load = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      const [c, s] = await Promise.all([
        api.get("/campaigns", { params: { branch_id: activeBranchId } }),
        api.get("/campaigns/stats", { params: { branch_id: activeBranchId } }),
      ]);
      setCampaigns(c.data); setStats(s.data);
    } catch (e) { /* ignore */ }
  }, [activeBranchId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!campaigns.some((c) => c.status === "sending" || c.status === "scheduled")) return;
    const t = setInterval(load, 3000); return () => clearInterval(t);
  }, [campaigns, load]);

  // wizard state
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [template, setTemplate] = useState(null);
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [audience, setAudience] = useState("all");
  const [tagId, setTagId] = useState("");
  const [csv, setCsv] = useState({ headers: [], rows: [] });
  const [csvName, setCsvName] = useState("");
  const [varMap, setVarMap] = useState([]);
  const [mediaUrl, setMediaUrl] = useState("");
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [sendMode, setSendMode] = useState("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [sending, setSending] = useState(false);

  const [metaLimit, setMetaLimit] = useState(null);

  const openWizard = async () => {
    setMetaLimit(null);
    api.get("/branches/meta-status").then(({ data }) => setMetaLimit(data?.branches?.[activeBranchId] || null)).catch(() => {});
    setStep(1); setName(""); setTemplate(null); setAudience("all"); setCsv({ headers: [], rows: [] });
    setCsvName(""); setVarMap([]); setMediaUrl(""); setSelectedIds([]); setContactSearch(""); setTagId("");
    setSendMode("now"); setScheduledAt(""); setOpen(true);
    try {
      const [t, c, g] = await Promise.all([
        api.get("/templates", { params: { branch_id: activeBranchId } }),
        api.get("/contacts", { params: { branch_id: activeBranchId } }),
        api.get("/tags", { params: { branch_id: activeBranchId } }),
      ]);
      setApprovedTemplates(t.data.filter((x) => (x.status || "").toUpperCase() === "APPROVED"));
      setContacts(c.data);
      setTags(g.data);
    } catch (e) { /* ignore */ }
  };

  // ---- campaign row actions (detail / resend / export) ----
  const [detail, setDetail] = useState(null);
  const [recips, setRecips] = useState([]);
  const [rSearch, setRSearch] = useState("");
  const [rStatus, setRStatus] = useState("all");
  const [detailLoading, setDetailLoading] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [downloadTarget, setDownloadTarget] = useState(null);
  const [exporting, setExporting] = useState("");

  const loadRecipients = useCallback(async (cid) => {
    setDetailLoading(true);
    try {
      const params = {};
      if (rSearch) params.search = rSearch;
      if (rStatus !== "all") params.status = rStatus;
      const { data } = await api.get(`/campaigns/${cid}/recipients`, { params });
      setRecips(data);
    } finally { setDetailLoading(false); }
  }, [rSearch, rStatus]);

  useEffect(() => { if (detail) loadRecipients(detail.id); }, [detail, loadRecipients]);

  const doResend = async () => {
    try {
      const { data } = await api.post(`/campaigns/${resendTarget.id}/resend`);
      toast.success(`Resending to ${data.resending} failed recipient(s)`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResendTarget(null); }
  };

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/campaigns/${downloadTarget.id}/export`, { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${downloadTarget.name || "campaign"}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(""); }
  };

  const rStatusBadge = (s) => {
    const map = { queued: "text-slate-300 bg-white/10", sent: "text-blue-400 bg-blue-500/10", delivered: "text-green-400 bg-green-500/10", read: "text-sky-400 bg-sky-500/10", failed: "text-red-400 bg-red-500/10" };
    return <span className={`text-[10px] uppercase font-bold rounded px-2 py-0.5 ${map[s] || "text-slate-300 bg-white/10"}`}>{s || "—"}</span>;
  };
  const fmtTs = (v) => (v ? new Date(v).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
  const fmtDur = (ms) => {
    const m = Math.max(0, Math.round(ms / 60000));
    return m < 60 ? `${m} min` : `${Math.floor(m / 60)} hr ${m % 60} min`;
  };
  const rate = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "—");
  const campaignTimes = (c) => {
    const items = [["Created", fmtTs(c.created_at)]];
    if (c.scheduled_at) items.push(["Scheduled for", fmtTs(c.scheduled_at)]);
    if (c.started_at) items.push(["Started", fmtTs(c.started_at)]);
    if (c.completed_at) items.push(["Finished", fmtTs(c.completed_at)]);
    if (c.started_at && c.completed_at) items.push(["Took", fmtDur(new Date(c.completed_at) - new Date(c.started_at))]);
    else if (c.started_at && c.status === "sending") items.push(["Running for", fmtDur(Date.now() - new Date(c.started_at))]);
    if (c.created_by) items.push(["By", c.created_by]);
    return items;
  };
  const campaignRates = (c) => [
    ["Pending", Math.max(0, c.total_count - c.sent_count - c.failed_count)],
    ["Delivery rate", rate(c.delivered_count, c.sent_count)],
    ["Read rate", rate(c.read_count, c.delivered_count)],
    ["Fail rate", rate(c.failed_count, c.total_count)],
  ];

  const activeContacts = useMemo(() => contacts.filter((c) => !c.opted_out), [contacts]);
  const filteredContacts = useMemo(() => {
    const s = contactSearch.toLowerCase();
    return activeContacts.filter((c) => !s || (c.name || "").toLowerCase().includes(s) || (c.mobile || "").includes(s));
  }, [activeContacts, contactSearch]);

  const pickTemplate = (t) => {
    setTemplate(t);
    setVarMap(Array.from({ length: t.variable_count }, () => ({ mode: "fixed", value: "" })));
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setCsvName(file.name); setCsv(parseCsv(await file.text())); setShowRemoved(false);
  };

  const onMediaFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingMedia(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMediaUrl(`${BACKEND_URL}${data.path}`);
      toast.success("Image uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploadingMedia(false); }
  };

  const csvCols = useMemo(() => ({
    mobile: csv.headers.find((h) => /mobile|phone|number/i.test(h)) || csv.headers[0],
    name: csv.headers.find((h) => /^(member[\s_]*|full[\s_]*|customer[\s_]*)?name$/i.test(h)) || csv.headers.find((h) => /name/i.test(h) && !/branch|template|file/i.test(h)),
  }), [csv]);

  // Clean the CSV once: keep the first row per number, set aside duplicates and bad numbers.
  const csvCheck = useMemo(() => {
    const seen = new Set();
    const valid = [], dups = [], invalid = [];
    csv.rows.forEach((r) => {
      const raw = r[csvCols.mobile];
      const digits = mobileDigits(raw);
      const item = { row: r, raw: raw || "", name: (csvCols.name && r[csvCols.name]) || "" };
      if (digits.length < 10) invalid.push(item);
      else if (seen.has(mobileKey(digits))) dups.push(item);
      else { seen.add(mobileKey(digits)); valid.push({ ...item, mobile: digits }); }
    });
    return { valid, dups, invalid };
  }, [csv, csvCols]);
  const [showRemoved, setShowRemoved] = useState(false);

  const buildVars = (row) => varMap.map((v) => (v.mode === "column" ? String(row?.[v.value] ?? "") : v.value));

  const recipients = useMemo(() => {
    if (audience === "csv") return csvCheck.valid.map((v) => ({ contact_id: null, mobile: v.mobile, variables: buildVars(v.row) }));
    const picked = audience === "all" ? activeContacts
      : audience === "select" ? activeContacts.filter((c) => selectedIds.includes(c.id))
      : activeContacts.filter((c) => tagId && (c.tags || []).includes(tagId));
    const seen = new Set();
    return picked.filter((c) => {
      const k = mobileKey(c.mobile);
      if (!k || seen.has(k)) return false;
      seen.add(k); return true;
    }).map((c) => ({ contact_id: c.id, mobile: c.mobile, variables: buildVars(null) }));
    // eslint-disable-next-line
  }, [audience, activeContacts, selectedIds, tagId, csvCheck, varMap]);

  const previewText = useMemo(() => {
    let txt = template?.body_text || "Select a template to preview.";
    varMap.forEach((v, i) => {
      let val = v.mode === "fixed" ? v.value : (csv.rows[0] ? csv.rows[0][v.value] : "");
      txt = txt.replace(new RegExp(`{{\\s*${i + 1}\\s*}}`, "g"), val || `{{${i + 1}}}`);
    });
    return txt;
  }, [template, varMap, csv]);

  const launch = async () => {
    setSending(true);
    try {
      const payload = {
        branch_id: activeBranchId, name, template_name: template.name, language: template.language,
        media_url: mediaUrl || null, recipients,
      };
      if (sendMode === "later" && scheduledAt) payload.scheduled_at = new Date(scheduledAt).toISOString();
      await api.post("/campaigns", payload);
      toast.success(sendMode === "later" ? "Campaign scheduled" : "Campaign started");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const canNext = (step === 1 && name.trim()) || (step === 2 && template) || step === 3 || (step === 4 && recipients.length > 0);
  const pct = (c) => (c.total_count ? Math.round(((c.sent_count + c.failed_count) / c.total_count) * 100) : 0);
  const toggleId = (id) => setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  if (open) {
    const hasMediaHeader = template && ["IMAGE", "VIDEO", "DOCUMENT"].includes((template.header_type || "").toUpperCase());
    const tagCount = tagId ? activeContacts.filter((c) => (c.tags || []).includes(tagId)).length : 0;
    const audiences = [
      { key: "all", icon: Users, title: "All Contacts", sub: `Send to all ${activeContacts.length} WhatsApp contacts on this branch` },
      { key: "select", icon: CheckSquare, title: "Specific Contacts", sub: "Handpick individual recipients" },
      { key: "tag", icon: TagIcon, title: "Segment by Tag", sub: `Target people with a label (${tags.length} tags)` },
      { key: "csv", icon: Upload, title: "Import List", sub: "CSV file with mobile + extra columns" },
    ];
    const card = "bg-[#141414] border border-white/10 rounded-2xl";
    const bigInput = "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-xl px-4 py-3 text-base transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
    const sectionHead = (Icon, title, sub) => (
      <div className="flex items-start gap-4 pb-5 mb-6 border-b border-white/10">
        <div className="h-12 w-12 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0"><Icon className="h-6 w-6 text-orange-500" /></div>
        <div><h2 className="font-heading text-xl font-bold text-white">{title}</h2><p className="text-sm text-slate-400 mt-1">{sub}</p></div>
      </div>
    );
    const varMapper = (allowColumn) => (
      <div className="space-y-3">
        {varMap.map((v, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-sm font-bold text-orange-500 w-14 shrink-0">{`{{${i + 1}}}`}</span>
            {allowColumn && (
              <select data-testid={`varmap-mode-${i}`} className="border border-white/20 bg-white/5 text-white rounded-xl px-3 py-3 text-base" value={v.mode} onChange={(e) => setVarMap(varMap.map((x, j) => j === i ? { ...x, mode: e.target.value, value: "" } : x))}>
                <option value="fixed">Fixed</option><option value="column">CSV column</option>
              </select>
            )}
            {v.mode === "column" && allowColumn ? (
              <select data-testid={`varmap-col-${i}`} className={bigInput} value={v.value} onChange={(e) => setVarMap(varMap.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}>
                <option value="">Choose column</option>{csv.headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            ) : (
              <input data-testid={`varmap-val-${i}`} className={bigInput} value={v.mode === "fixed" ? v.value : ""} onChange={(e) => setVarMap(varMap.map((x, j) => j === i ? { ...x, mode: "fixed", value: e.target.value } : x))} placeholder={`Value for {{${i + 1}}}`} />
            )}
          </div>
        ))}
      </div>
    );
    const phonePreview = (
      <div className={`${card} p-5 lg:sticky lg:top-4`}>
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400 mb-4 flex items-center gap-2"><Eye className="h-4 w-4" /> Real-time preview</p>
        <div className="mx-auto w-full max-w-[280px] rounded-[2.2rem] border-[6px] border-slate-800 bg-slate-800 overflow-hidden shadow-2xl">
          <div className="bg-[#075e54] px-4 py-3 flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">AG</div>
            <div><p className="text-white text-sm font-semibold leading-tight">Adiyash Gym</p><p className="text-white/70 text-[11px]">Business Account</p></div>
          </div>
          <div className="p-3 min-h-[380px] max-h-[520px] overflow-y-auto" style={{ backgroundColor: "#efe7dd", backgroundImage: "radial-gradient(rgba(0,0,0,0.06) 1px, transparent 1px)", backgroundSize: "14px 14px" }}>
            <div className="text-center mb-3"><span className="text-[10px] bg-sky-100 text-slate-600 rounded px-2 py-0.5 uppercase font-semibold shadow-sm">Today</span></div>
            {!template ? (
              <p className="text-center text-sm text-slate-500 mt-16 px-4">Pick a template to see your message here.</p>
            ) : (
              <div className="max-w-[95%]">
                <div className="bg-white rounded-lg rounded-tl-none shadow-sm p-1.5">
                  {mediaUrl ? <img src={mediaUrl} alt="header" className="w-full h-36 object-cover rounded-md mb-1" onError={(e) => { e.target.style.display = "none"; }} />
                    : hasMediaHeader && <div className="w-full h-28 rounded-md mb-1 bg-slate-200 flex flex-col items-center justify-center text-slate-400"><ImageIcon className="h-8 w-8" /><span className="text-[10px] mt-1">Add a header image in step 3</span></div>}
                  <div className="px-1.5 pt-0.5">
                    {template.header_text && (template.header_type || "").toUpperCase() === "TEXT" && <p className="text-sm font-bold text-slate-900 mb-1">{template.header_text}</p>}
                    <p data-testid="preview-body" className="text-[13px] leading-snug text-slate-800 whitespace-pre-wrap break-words">{previewText}</p>
                    {template.footer_text && <p className="text-[11px] text-slate-500 mt-1.5">{template.footer_text}</p>}
                    <p className="text-[10px] text-slate-400 text-right mt-0.5 flex items-center justify-end gap-1">
                      {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      <CheckCheck className="h-3.5 w-3.5 text-sky-500" />
                    </p>
                  </div>
                </div>
                {(template.buttons || []).filter((b) => b.text).map((b, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-sm mt-1 py-2 text-center text-[13px] font-medium text-sky-600 flex items-center justify-center gap-1.5">
                    {(b.type || "").toUpperCase() === "URL" && <ArrowRight className="h-3.5 w-3.5 -rotate-45" />}
                    {b.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
    const steps = [
      { n: 1, title: "Basic info", sub: "Campaign name" },
      { n: 2, title: "Template", sub: "Approved message" },
      { n: 3, title: "Variables & media", sub: "Dynamic content" },
      { n: 4, title: "Recipients", sub: "Target audience" },
      { n: 5, title: "Schedule", sub: "Timing and launch" },
    ];

    return (
      <div data-testid="campaign-wizard" className="pb-10">
        <button onClick={() => setOpen(false)} className="flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Back to campaigns</button>
        <h1 className="font-heading text-4xl tracking-tight font-black text-white">Create campaign</h1>
        <p className="text-base text-slate-400 mt-2 mb-6">Follow the steps below to set up and launch your WhatsApp broadcast.</p>

        <div className={`${card} grid grid-cols-5 overflow-hidden mb-6`}>
          {steps.map((s) => {
            const done = s.n < step; const active = s.n === step;
            return (
              <button key={s.n} disabled={s.n > step} onClick={() => setStep(s.n)}
                className={`text-left px-4 py-4 border-r border-white/10 last:border-r-0 transition-colors ${active ? "bg-orange-500/10 border-b-2 border-b-orange-500" : "hover:bg-white/5 disabled:hover:bg-transparent"}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${done ? "bg-green-500 text-white" : active ? "bg-orange-500 text-white" : "bg-white/10 text-slate-400"}`}>
                    {done ? <CheckCheck className="h-4 w-4" /> : s.n}
                  </span>
                  <span className={`text-sm font-bold uppercase tracking-wide hidden md:inline ${done ? "text-green-400" : active ? "text-orange-400" : "text-slate-400"}`}>{s.title}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1.5 hidden lg:block">{s.sub}</p>
              </button>
            );
          })}
        </div>

        <div className={step >= 2 ? "grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start" : ""}>
          <div className={`${card} overflow-hidden`}>
            <div className="p-6 md:p-8">
              {step === 1 && (
                <>
                  {sectionHead(Megaphone, "Campaign details", "Start with a clear name so your team can find this broadcast later.")}
                  <label className="text-sm font-bold uppercase tracking-wide text-slate-300">Campaign name <span className="text-orange-500">*</span></label>
                  <input data-testid="wizard-name-input" className={`mt-2 ${bigInput}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="E.g. Diwali Offer 2026 — Marol" autoFocus />
                  <p className="text-sm text-slate-500 mt-2">Only visible inside this app — not sent to members.</p>
                </>
              )}

              {step === 2 && (
                <>
                  {sectionHead(Send, "Choose a template", "Pick the approved WhatsApp template for this broadcast.")}
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm font-bold uppercase tracking-wide text-slate-300">Select message template</p>
                    <span className="text-xs font-bold uppercase bg-white/10 text-slate-300 rounded-full px-3 py-1">{approvedTemplates.length} approved</span>
                  </div>
                  {approvedTemplates.length === 0 ? <p className="text-base text-slate-400 py-10 text-center">No APPROVED templates on this branch.</p> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
                      {approvedTemplates.map((t) => {
                        const cat = (t.category || "").toUpperCase();
                        const ht = (t.header_type || "NONE").toUpperCase();
                        return (
                          <button key={t.id} data-testid={`wizard-template-${t.id}`} onClick={() => pickTemplate(t)}
                            className={`text-left border rounded-xl p-4 flex flex-col transition-all duration-150 ${template?.id === t.id ? "border-orange-500 bg-orange-500/10 ring-2 ring-orange-500/40" : "border-white/10 hover:border-white/25 hover:bg-white/5"}`}>
                            <div className="flex items-center gap-2 mb-2">
                              {cat && <span className={`text-[11px] font-bold rounded px-2 py-0.5 ${cat === "MARKETING" ? "bg-orange-500/15 text-orange-400" : "bg-blue-500/15 text-blue-400"}`}>{cat.charAt(0) + cat.slice(1).toLowerCase()}</span>}
                              <span className="text-xs text-slate-400">{t.language}</span>
                            </div>
                            <p className="font-bold text-base text-white break-all">{t.name}</p>
                            <p className="text-sm text-slate-400 mt-2 line-clamp-3 flex-1">{t.body_text}</p>
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                              <span className="text-xs text-slate-400 flex items-center gap-1">{ht === "NONE" ? <FileText className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}{ht === "NONE" ? "no header" : `${ht.toLowerCase()} header`}</span>
                              <span className="text-[11px] font-bold rounded px-2 py-0.5 bg-green-500/15 text-green-400">Approved</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {step === 3 && (
                <>
                  {sectionHead(FileText, "Campaign configuration", "Fill in the template's variables and header image.")}
                  <div className="space-y-6">
                    <div className="border border-white/10 rounded-xl p-5">
                      <p className="text-base font-bold text-orange-400 mb-1">Variables</p>
                      {template?.variable_count > 0 ? (
                        <>
                          <p className="text-sm text-slate-400 mb-4">Same value for everyone. For per-person values, choose "Import List" in the next step and map CSV columns there.</p>
                          {varMapper(false)}
                        </>
                      ) : <p className="text-sm text-slate-400">This template has no variables.</p>}
                    </div>
                    <div className="border border-white/10 rounded-xl p-5">
                      <p className="text-base font-bold text-orange-400 mb-1">Media header {hasMediaHeader ? "" : "(optional)"}</p>
                      <p className="text-sm text-slate-400 mb-4">Upload an image or paste a public HTTPS link.</p>
                      <label className="flex items-center justify-center gap-2 border-2 border-dashed border-white/20 bg-white/5 rounded-xl py-5 cursor-pointer hover:border-orange-400 transition-all duration-150">
                        {uploadingMedia ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5 text-slate-400" />}
                        <span className="text-base font-semibold text-slate-300">{mediaUrl ? "Upload replacement" : "Upload image"}</span>
                        <input data-testid="wizard-media-file" type="file" accept="image/*" className="hidden" onChange={onMediaFile} />
                      </label>
                      <p className="text-center text-xs font-bold uppercase text-slate-500 my-3">or paste URL</p>
                      <input data-testid="wizard-media-url" className={bigInput} value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} placeholder="https://example.com/banner.jpg" />
                    </div>
                  </div>
                </>
              )}

              {step === 4 && (
                <>
                  {sectionHead(Users, "Recipients", "Choose who will receive this campaign message.")}
                  <div className="grid grid-cols-2 gap-4">
                    {audiences.map(({ key, icon: Icon, title, sub }) => (
                      <button key={key} data-testid={`audience-${key}`} onClick={() => setAudience(key)}
                        className={`border rounded-xl p-5 flex flex-col items-center text-center transition-all duration-150 ${audience === key ? "border-orange-500 bg-orange-500/10 ring-2 ring-orange-500/40" : "border-white/10 hover:border-white/25 hover:bg-white/5"}`}>
                        <div className={`h-14 w-14 rounded-xl flex items-center justify-center mb-3 ${audience === key ? "bg-orange-500" : "bg-white/10"}`}><Icon className={`h-7 w-7 ${audience === key ? "text-white" : "text-slate-300"}`} /></div>
                        <p className={`text-base font-bold uppercase tracking-wide ${audience === key ? "text-orange-400" : "text-white"}`}>{title}</p>
                        <p className="text-sm text-slate-400 mt-1">{sub}</p>
                      </button>
                    ))}
                  </div>

                  <div className="mt-6">
                    {audience === "tag" && (
                      tags.length === 0 ? <p className="text-base text-slate-400">No tags yet — create one on the Tags page first.</p> : (
                        <select data-testid="audience-tag-select" className={bigInput} value={tagId} onChange={(e) => setTagId(e.target.value)}>
                          <option value="">Choose a tag</option>
                          {tags.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.contact_count})</option>)}
                        </select>
                      )
                    )}

                    {audience === "select" && (
                      <div className="border border-white/10 rounded-xl">
                        <div className="p-3 border-b border-white/10 relative">
                          <Search className="h-5 w-5 text-slate-400 absolute left-6 top-1/2 -translate-y-1/2" />
                          <input data-testid="contact-search" className={`${bigInput} pl-11`} placeholder="Search name or mobile" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} />
                        </div>
                        <div className="max-h-72 overflow-y-auto">
                          {filteredContacts.map((c) => (
                            <button key={c.id} data-testid={`pick-contact-${c.id}`} onClick={() => toggleId(c.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5">
                              {selectedIds.includes(c.id) ? <CheckSquare className="h-5 w-5 text-orange-500" /> : <Square className="h-5 w-5 text-slate-600" />}
                              <span className="text-base text-white">{c.name}</span><span className="text-sm text-slate-400">{c.mobile}</span>
                            </button>
                          ))}
                          {filteredContacts.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No contacts</p>}
                        </div>
                      </div>
                    )}

                    {audience === "csv" && (
                      <div className="space-y-5">
                        <label className="flex items-center justify-center gap-2 border-2 border-dashed border-white/20 bg-white/5 rounded-xl py-6 cursor-pointer hover:border-orange-400">
                          <Upload className="h-5 w-5 text-slate-400" /><span className="text-base font-semibold text-slate-300">{csvName || "Choose .csv file"}</span>
                          <input data-testid="wizard-csv-input" type="file" accept=".csv,text/csv" className="hidden" onChange={onCsv} />
                        </label>
                        {csv.rows.length > 0 && (
                          <div data-testid="csv-summary" className="border border-white/10 rounded-xl overflow-hidden">
                            <p className="px-5 py-3 text-sm font-bold uppercase tracking-wide text-slate-300 bg-white/5 border-b border-white/10">File check</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4">
                              {[["Rows in file", csv.rows.length, "text-white"],
                                ["Will receive", csvCheck.valid.length, "text-green-400"],
                                ["Duplicates removed", csvCheck.dups.length, csvCheck.dups.length ? "text-orange-400" : "text-white"],
                                ["Invalid / empty skipped", csvCheck.invalid.length, csvCheck.invalid.length ? "text-red-400" : "text-white"]].map(([k, v, cls]) => (
                                <div key={k} className="rounded-lg bg-white/5 px-4 py-3">
                                  <p className="text-xs uppercase tracking-wide font-bold text-slate-500">{k}</p>
                                  <p className={`text-2xl font-black ${cls}`}>{v}</p>
                                </div>
                              ))}
                            </div>
                            <p className="px-5 pb-3 text-sm text-slate-400">
                              Mobile column: <b className="text-slate-200">{csvCols.mobile}</b>{csvCols.name ? <> · Name column: <b className="text-slate-200">{csvCols.name}</b></> : null} · Columns: {csv.headers.join(", ")}
                            </p>
                            {(csvCheck.dups.length > 0 || csvCheck.invalid.length > 0) && (
                              <div className="border-t border-white/10">
                                <button data-testid="csv-show-removed" type="button" onClick={() => setShowRemoved((s) => !s)} className="w-full text-left px-5 py-3 text-sm font-semibold text-orange-400 hover:bg-white/5">
                                  {showRemoved ? "Hide" : "Show"} removed numbers ({csvCheck.dups.length + csvCheck.invalid.length})
                                </button>
                                {showRemoved && (
                                  <div className="max-h-56 overflow-y-auto divide-y divide-white/10">
                                    {[...csvCheck.dups.map((d) => ({ ...d, why: "Duplicate" })), ...csvCheck.invalid.map((d) => ({ ...d, why: d.raw ? "Invalid number" : "Empty" }))].map((d, i) => (
                                      <div key={i} className="flex items-center justify-between gap-3 px-5 py-2 text-sm">
                                        <span className="text-white truncate">{d.name || "—"}</span>
                                        <span className="text-slate-300">{d.raw || "—"}</span>
                                        <span className={d.why === "Duplicate" ? "text-orange-400" : "text-red-400"}>{d.why}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {csv.rows.length > 0 && (() => {
                          const mobileCol = csvCols.mobile;
                          const nameCol = csvCols.name;
                          return (
                            <div data-testid="csv-preview" className="border border-white/10 rounded-xl overflow-hidden">
                              <p className="px-5 py-3 text-sm font-bold uppercase tracking-wide text-slate-300 bg-white/5 border-b border-white/10">First {Math.min(3, csv.rows.length)} contacts in this file</p>
                              <table className="w-full text-base">
                                <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                                  <th className="px-5 py-2 font-bold">Name</th><th className="px-5 py-2 font-bold">Mobile</th>
                                </tr></thead>
                                <tbody className="divide-y divide-white/10">
                                  {csv.rows.slice(0, 3).map((r, i) => (
                                    <tr key={i}>
                                      <td className="px-5 py-3 text-white">{(nameCol && r[nameCol]) || <span className="text-slate-500">—</span>}</td>
                                      <td className="px-5 py-3 text-slate-300">{r[mobileCol] || <span className="text-red-400">missing</span>}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {csv.rows.length > 3 && <p className="px-5 py-3 text-sm text-slate-400 border-t border-white/10">…and {csv.rows.length - 3} more</p>}
                            </div>
                          );
                        })()}
                        {csv.headers.length > 0 && template?.variable_count > 0 && (
                          <div className="border border-white/10 rounded-xl p-5">
                            <p className="text-base font-bold text-orange-400 mb-4">Map variables to CSV columns</p>
                            {varMapper(true)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className={`mt-6 rounded-xl border p-5 flex items-center gap-4 ${recipients.length ? "border-green-500/40 bg-green-500/5" : "border-white/10 bg-white/5"}`}>
                    <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center"><Users className="h-6 w-6 text-orange-500" /></div>
                    <div>
                      <p data-testid="review-recipient-count" className="text-lg font-bold text-white">Targeting {recipients.length} contact{recipients.length === 1 ? "" : "s"}</p>
                      <p className="text-sm text-slate-400">
                        {audience === "csv" ? "Each number gets it once — duplicates and invalid numbers are removed." : `${audience === "tag" && tagId ? `${tagCount} contacts have this tag. ` : ""}Opted-out contacts are skipped automatically.`}
                      </p>
                      {metaLimit?.limit_label && (
                        metaLimit.limit && recipients.length > metaLimit.limit ? (
                          <p data-testid="limit-warning" className="text-sm font-semibold text-orange-400 mt-1.5">
                            ⚠️ Daily limit {metaLimit.limit_label}{metaLimit.limit_scope === "shared" ? " (shared by all branches)" : ""}: only {metaLimit.limit_label} new people per 24h — the rest will fail; use Resend Failed tomorrow.
                          </p>
                        ) : (
                          <p data-testid="limit-ok" className="text-sm font-semibold text-green-400 mt-1.5 flex items-center gap-1.5">
                            <CheckCheck className="h-4 w-4" /> Within daily limit of {metaLimit.limit_label}{metaLimit.limit_scope === "shared" ? " (shared by all branches)" : ""}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </>
              )}

              {step === 5 && (
                <>
                  {sectionHead(Clock, "Schedule & launch", "Check the summary and choose when to send.")}
                  <div className="border border-white/10 rounded-xl divide-y divide-white/10 mb-6">
                    <div className="flex justify-between px-5 py-4 text-base"><span className="text-slate-400">Campaign</span><b className="text-white">{name}</b></div>
                    <div className="flex justify-between px-5 py-4 text-base"><span className="text-slate-400">Template</span><b className="text-white">{template?.name}</b></div>
                    <div className="flex justify-between px-5 py-4 text-base"><span className="text-slate-400">Recipients</span><b className="text-orange-500">{recipients.length}</b></div>
                  </div>
                  <p className="text-sm font-bold uppercase tracking-wide text-slate-300 mb-3">When to send</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[["now", Send, "Send immediately", "Starts right after you launch"], ["later", Clock, "Schedule for later", "Pick a date and time"]].map(([k, Icon, t, sub]) => (
                      <button key={k} data-testid={k === "now" ? "send-now-option" : "schedule-option"} onClick={() => setSendMode(k)}
                        className={`border rounded-xl p-5 text-left flex items-center gap-4 ${sendMode === k ? "border-orange-500 bg-orange-500/10 ring-2 ring-orange-500/40" : "border-white/10 hover:bg-white/5"}`}>
                        <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${sendMode === k ? "bg-orange-500" : "bg-white/10"}`}><Icon className="h-6 w-6 text-white" /></div>
                        <div><p className="font-bold text-base text-white">{t}</p><p className="text-sm text-slate-400">{sub}</p></div>
                      </button>
                    ))}
                  </div>
                  {sendMode === "later" && (
                    <input data-testid="schedule-datetime" type="datetime-local" className={`mt-4 ${bigInput}`} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-between px-6 md:px-8 py-4 border-t border-white/10 bg-white/[0.02]">
              <button className={`${secondaryBtn} px-5 py-2.5 text-base`} onClick={() => (step === 1 ? setOpen(false) : setStep(step - 1))}>
                {step === 1 ? "Cancel" : <><ArrowLeft className="h-4 w-4" /> Back</>}
              </button>
              {step < 5 ? (
                <button data-testid="wizard-next-button" className={`${primaryBtn} px-6 py-2.5 text-base`} onClick={() => setStep(step + 1)} disabled={!canNext}>Continue <ArrowRight className="h-4 w-4" /></button>
              ) : (
                <button data-testid="wizard-send-button" className={`${primaryBtn} px-6 py-2.5 text-base`} onClick={launch} disabled={sending || recipients.length === 0 || (sendMode === "later" && !scheduledAt)}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (sendMode === "later" ? <Clock className="h-4 w-4" /> : <Send className="h-4 w-4" />)}
                  {sendMode === "later" ? "Schedule Campaign" : "Launch Now"}
                </button>
              )}
            </div>
          </div>
          {step >= 2 && phonePreview}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="broadcasting-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-black text-white">Broadcasting</h1>
          <p className="text-sm text-slate-500 mt-2">Bulk WhatsApp campaigns for this branch.</p>
        </div>
        <button data-testid="create-campaign-button" className={primaryBtn} onClick={openWizard}><Plus className="h-4 w-4" /> Create Campaign</button>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
      >
        <StatCard testid="stat-total-campaigns" icon={Megaphone} label="Total Campaigns" value={stats.total_campaigns} />
        <StatCard testid="stat-total-sent" icon={Send} label="Total Sent" value={stats.total_sent} />
        <StatCard testid="stat-delivered" icon={CheckCheck} label="Delivered" value={stats.delivered} />
        <StatCard testid="stat-read" icon={Eye} label="Read" value={stats.read} />
      </motion.div>

      <div className="border border-white/10 rounded-lg bg-[#141414] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10"><h3 className="font-heading font-bold text-white">Past Campaigns</h3></div>
        {campaigns.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">No campaigns yet.</div>
        ) : (
          <div className="divide-y divide-white/10">
            {campaigns.map((c) => (
              <div key={c.id} data-testid={`campaign-row-${c.id}`} className="px-5 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-white truncate">{c.name}</p>
                    <p className="text-xs text-slate-500">Template: {c.template_name}{c.scheduled_at ? ` · scheduled ${new Date(c.scheduled_at).toLocaleString()}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={`text-[10px] tracking-[0.12em] uppercase font-bold rounded px-2 py-0.5 ${c.status === "sending" ? "text-amber-400 bg-amber-500/10" : c.status === "scheduled" ? "text-blue-400 bg-blue-500/10" : "text-green-400 bg-green-500/10"}`}>{c.status}</span>
                    <button data-testid={`campaign-info-${c.id}`} title="Details" onClick={() => { setRSearch(""); setRStatus("all"); setDetail(c); }} className="p-1.5 rounded-md text-slate-500 hover:bg-white/10 transition-all duration-150"><Info className="h-4 w-4" /></button>
                    <button data-testid={`campaign-resend-${c.id}`} title="Resend failed" disabled={!c.failed_count} onClick={() => setResendTarget(c)} className="p-1.5 rounded-md text-orange-600 hover:bg-orange-500/10 transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"><RotateCw className="h-4 w-4" /></button>
                    <button data-testid={`campaign-download-${c.id}`} title="Export" onClick={() => setDownloadTarget(c)} className="p-1.5 rounded-md text-slate-500 hover:bg-white/10 transition-all duration-150"><Download className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-orange-500 transition-all" style={{ width: `${pct(c)}%` }} /></div>
                <div className="flex flex-wrap gap-4 mt-2 text-xs">
                  <span className="text-slate-500">Total <b className="text-white">{c.total_count}</b></span>
                  <span className="text-slate-500">Sent <b className="text-white">{c.sent_count}</b></span>
                  <span className="text-slate-500">Delivered <b className="text-white">{c.delivered_count}</b></span>
                  <span className="text-slate-500">Read <b className="text-white">{c.read_count}</b></span>
                  <span className="text-slate-500">Failed <b className="text-red-600">{c.failed_count}</b></span>
                  {campaignRates(c).map(([k, v]) => <span key={k} className="text-slate-500">{k} <b className="text-white">{v}</b></span>)}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs">
                  {campaignTimes(c).map(([k, v]) => <span key={k} className="text-slate-500">{k} <b className="text-slate-300">{v}</b></span>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Campaign detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="campaign-detail-dialog" className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading">{detail?.name} — Recipients</DialogTitle></DialogHeader>
          {detail && (
            <div data-testid="detail-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {[["Template", detail.template_name], ["Status", detail.status], ["Total", detail.total_count], ["Sent", detail.sent_count],
                ["Delivered", detail.delivered_count], ["Read", detail.read_count], ["Failed", detail.failed_count],
                ...campaignRates(detail), ...campaignTimes(detail)].map(([k, v]) => (
                <div key={k} className="rounded-md bg-white/5 px-3 py-2">
                  <p className="text-[10px] tracking-[0.12em] uppercase font-bold text-slate-500">{k}</p>
                  <p className="text-sm font-semibold text-white truncate">{v}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="detail-search" className={`${inputCls} pl-9`} placeholder="Search name or mobile" value={rSearch} onChange={(e) => setRSearch(e.target.value)} />
            </div>
            <Select value={rStatus} onValueChange={setRStatus}>
              <SelectTrigger data-testid="detail-status-filter" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{["all", "queued", "sent", "delivered", "read", "failed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="border border-white/10 rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-white/5">
                  {["Name", "Mobile", "Status", "Failure", "Timestamp"].map((h) => <TableHead key={h} className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailLoading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>
                ) : recips.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-slate-400">No recipients match.</TableCell></TableRow>
                ) : recips.map((r) => (
                  <TableRow key={r.id} data-testid={`recipient-row-${r.id}`}>
                    <TableCell className="font-medium text-white">{r.name}</TableCell>
                    <TableCell className="text-slate-300">{r.mobile}</TableCell>
                    <TableCell>{rStatusBadge(r.status)}</TableCell>
                    <TableCell className="text-xs text-red-600 max-w-[180px] truncate">{r.failure_reason || ""}</TableCell>
                    <TableCell className="text-sm text-slate-500 whitespace-nowrap">{fmtTs(r.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resend confirm */}
      <AlertDialog open={!!resendTarget} onOpenChange={(o) => !o && setResendTarget(null)}>
        <AlertDialogContent data-testid="resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Resend to failed recipients?</AlertDialogTitle>
            <AlertDialogDescription>
              This will re-send "{resendTarget?.name}" to its <b>{resendTarget?.failed_count}</b> failed recipient(s) using the same template and variables, updating their rows in place.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-resend" className="bg-orange-600 hover:bg-orange-700" onClick={doResend}>Resend</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Download modal */}
      <Dialog open={!!downloadTarget} onOpenChange={(o) => !o && setDownloadTarget(null)}>
        <DialogContent data-testid="download-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Export recipients</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-2 py-2">
            <button data-testid="export-xlsx" onClick={() => doExport("xlsx")} disabled={!!exporting} className="flex items-center gap-3 border border-white/10 rounded-md px-4 py-3 hover:bg-white/5 transition-all duration-150 disabled:opacity-60">
              {exporting === "xlsx" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sheet className="h-5 w-5 text-green-600" />}
              <span className="font-semibold text-sm">Excel (.xlsx)</span>
            </button>
            <button data-testid="export-csv" onClick={() => doExport("csv")} disabled={!!exporting} className="flex items-center gap-3 border border-white/10 rounded-md px-4 py-3 hover:bg-white/5 transition-all duration-150 disabled:opacity-60">
              {exporting === "csv" ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileSpreadsheet className="h-5 w-5 text-slate-300" />}
              <span className="font-semibold text-sm">CSV (.csv)</span>
            </button>
            <button data-testid="export-pdf" onClick={() => doExport("pdf")} disabled={!!exporting} className="flex items-center gap-3 border border-white/10 rounded-md px-4 py-3 hover:bg-white/5 transition-all duration-150 disabled:opacity-60">
              {exporting === "pdf" ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5 text-red-600" />}
              <span className="font-semibold text-sm">PDF (.pdf)</span>
            </button>
            <button data-testid="export-png" onClick={() => downloadCampaignPng(downloadTarget, activeBranch?.name, campaignTimes(downloadTarget))} disabled={!!exporting} className="flex items-center gap-3 border border-white/10 rounded-md px-4 py-3 hover:bg-white/5 transition-all duration-150 disabled:opacity-60">
              <ImageIcon className="h-5 w-5 text-orange-500" />
              <span className="font-semibold text-sm">Report image (.png) — for WhatsApp</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
